import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lead-tracker-96/artifacts/g69u3ad3_D%20Nexus%20Logo.png";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
          <img src={LOGO_URL} alt="Dnexus Media" className="h-10" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-500 mb-8">Last updated: February 21, 2026</p>

          <div className="prose prose-slate max-w-none">
            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">1. Introduction</h2>
            <p className="text-slate-600 mb-4">
              Welcome to Dnexus Media CRM ("we," "our," or "us"). We are committed to protecting your personal information 
              and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our customer relationship management platform.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">2. Information We Collect</h2>
            <p className="text-slate-600 mb-4">We collect information that you provide directly to us, including:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>Account Information:</strong> Name, email address, password, phone number, and profile picture</li>
              <li><strong>Business Information:</strong> Company name, business address, and industry type</li>
              <li><strong>Lead Data:</strong> Contact information of your leads and customers that you input into the system</li>
              <li><strong>Communication Data:</strong> Messages, emails, and interactions within the platform</li>
              <li><strong>Integration Data:</strong> Data from connected services like Facebook, WhatsApp, Google Sheets, and email providers</li>
              <li><strong>Usage Data:</strong> How you interact with our platform, features used, and performance metrics</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Provide, maintain, and improve our CRM services</li>
              <li>Process transactions and manage your account</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Respond to your comments, questions, and customer service requests</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, investigate, and prevent fraudulent transactions and abuse</li>
              <li>Personalize and improve your experience</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">4. Information Sharing</h2>
            <p className="text-slate-600 mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
              <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">5. Third-Party Integrations</h2>
            <p className="text-slate-600 mb-4">
              Our platform integrates with third-party services including Facebook, WhatsApp, Google, and email providers. 
              When you connect these services:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>We access only the data necessary to provide the integration features</li>
              <li>Your credentials are encrypted and stored securely</li>
              <li>You can disconnect integrations at any time from the Integrations page</li>
              <li>Each third-party service has its own privacy policy that governs their data practices</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">6. Data Security</h2>
            <p className="text-slate-600 mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication mechanisms</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and audit logging</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">7. Data Retention</h2>
            <p className="text-slate-600 mb-4">
              We retain your personal information for as long as your account is active or as needed to provide you services. 
              You can request deletion of your data at any time by contacting us or using the data deletion feature.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">8. Your Rights</h2>
            <p className="text-slate-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Access and receive a copy of your personal data</li>
              <li>Rectify or update your personal information</li>
              <li>Request deletion of your personal data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">9. Cookies and Tracking</h2>
            <p className="text-slate-600 mb-4">
              We use cookies and similar tracking technologies to track activity on our platform and hold certain information. 
              You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-600 mb-4">
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal 
              information from children under 18.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">11. Changes to This Policy</h2>
            <p className="text-slate-600 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
              Privacy Policy on this page and updating the "Last updated" date.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">12. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg text-slate-600">
              <p><strong>Dnexus Media</strong></p>
              <p>Email: privacy@dnexusmedia.com</p>
              <p>Website: https://crm.dnexusmedia.com</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} Dnexus Media. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
